import { useRef, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useAuth } from '@/utils/AuthContext';
import { useLanguage } from '@/i18n/LanguageContext';
import { useGenerationPolling } from '@/hooks/useGenerationPolling';
import { GenerationProgress } from '@/components/shared/GenerationProgress';
import { AiItemsList } from '@/components/shared/AiItemsList';
import { ConfirmModal } from '@/components/shared/ConfirmModal';
import { MethodologyGenerateForm } from '@/components/methodologies/MethodologyGenerateForm';
import { MethodologyResultPanel } from '@/components/methodologies/MethodologyResultPanel';
import { projectsApi, personasApi, methodologiesApi } from '@/utils/api-pb';
import type { Project, Persona } from '@/utils/pocketbase';
import type { CatalogMethod, ContextOverrides, MethodologyArtifact } from '@/types/methodologies';

interface MethodologyWorkspaceProps {
  /** Registry id, e.g. "swot", "cjm" — matches `MethodologyDefinition.id` on the backend. */
  methodId: string;
  /** Record to show first — the mentor chat links to a specific result via `?recordId=`. */
  initialRecordId?: string;
}

/**
 * Self-contained methodology UI: project/persona pick, inputs, generation with
 * polling, history and the result view. No route dependency — `/methods/:id`
 * (MethodPage) and, later, the `/research` tabs (F-03) both just mount this
 * with a `methodId`.
 */
export function MethodologyWorkspace({ methodId, initialRecordId }: MethodologyWorkspaceProps) {
  const { t } = useTranslation('methodologies');
  const { isFree: isFreeTier, subscriptionTier } = useAuth();
  // The catalogue is paid-only: the student plan sees the same paywall as free.
  const isFree = isFreeTier || subscriptionTier === 'student';
  const { lang } = useLanguage();

  const [catalog, setCatalog] = useState<CatalogMethod[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [artifacts, setArtifacts] = useState<MethodologyArtifact[]>([]);
  const [selectedArtifact, setSelectedArtifact] = useState<MethodologyArtifact | null>(null);

  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [selectedPersonaId, setSelectedPersonaId] = useState('');
  // One entry per non-project/persona input field (`focus`, but also
  // `hypotheses`/`goal`/`material`/… for the F-03 research methodologies) —
  // keyed by `MethodInputField.key`, same shape the generate request sends.
  const [inputValues, setInputValues] = useState<Record<string, string>>({});
  const handleInputChange = (key: string, value: string) =>
    setInputValues((prev) => ({ ...prev, [key]: value }));
  // Only deviations from "latest of each source"; the picker resets it on project change.
  const [contextOverrides, setContextOverrides] = useState<ContextOverrides>({});

  // Read by the polling callbacks: a running interval keeps the closure it was
  // started with, so the live methodology has to come from a ref.
  const activeMethodIdRef = useRef(methodId);

  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [hasLoadError, setHasLoadError] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [artifactToDelete, setArtifactToDelete] = useState<string | null>(null);

  const catalogMethod = catalog.find((m) => m.id === methodId);
  const translationRef = useRef(t);
  translationRef.current = t;

  const loadData = useCallback(async () => {
    setIsInitialLoading(true);
    setHasLoadError(false);
    try {
      const [catalogResult, projectsResult, artifactsResult] = await Promise.allSettled([
        methodologiesApi.catalog(),
        projectsApi.list(),
        methodologiesApi.list(undefined, methodId),
      ]);

      if (
        catalogResult.status === 'rejected'
        || projectsResult.status === 'rejected'
        || artifactsResult.status === 'rejected'
      ) {
        if (catalogResult.status === 'rejected') {
          console.error('Error loading methodology catalog:', catalogResult.reason);
        }
        if (projectsResult.status === 'rejected') {
          console.error('Error loading projects:', projectsResult.reason);
        }
        if (artifactsResult.status === 'rejected') {
          console.error('Error loading methodology artifacts:', artifactsResult.reason);
        }
        setHasLoadError(true);
        toast.error(translationRef.current('toast.loadError'));
        return;
      }

      setCatalog(catalogResult.value);
      setProjects(projectsResult.value);
      setArtifacts(artifactsResult.value);
      const requested = initialRecordId
        ? artifactsResult.value.find((artifact) => artifact.id === initialRecordId)
        : undefined;
      setSelectedArtifact((prev) => (
        prev
          ?? requested
          ?? artifactsResult.value.find((artifact) => artifact.status === 'pending')
          ?? artifactsResult.value[0]
          ?? null
      ));
    } finally {
      setIsInitialLoading(false);
    }
  }, [methodId, initialRecordId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Default to the first project once the list is in, and reset it (and the
  // persona pick) whenever a different methodology is mounted.
  useEffect(() => {
    setSelectedProjectId((prev) => prev || projects[0]?.id || '');
  }, [projects]);

  // Personas depend on the selected project — only fetched when the
  // methodology actually has a personaId input (CJM in wave A).
  useEffect(() => {
    setPersonas([]);
    setSelectedPersonaId('');

    const needsPersona = catalogMethod?.inputs.some((field) => field.key === 'personaId');
    if (!needsPersona || !selectedProjectId) {
      return;
    }
    // A slow response for the previous project must not overwrite the current
    // list: CJM pins a persona, so a wrong list means the wrong context.
    let ignore = false;
    personasApi.list(selectedProjectId)
      .then((list) => { if (!ignore) setPersonas(list); })
      .catch((error) => {
        if (ignore) return;
        console.error('Error loading personas:', error);
        setPersonas([]);
      });
    return () => { ignore = true; };
  }, [selectedProjectId, catalogMethod]);

  const isForActiveMethod = (record: MethodologyArtifact) =>
    !record.method || record.method === activeMethodIdRef.current;

  const { startPolling, stopPolling } = useGenerationPolling({
    collection: 'methodology_artifacts',
    onCompleted: (record: MethodologyArtifact) => {
      if (!isForActiveMethod(record)) return;
      setIsGenerating(false);
      setArtifacts((prev) => prev.map((a) => (a.id === record.id ? record : a)));
      setSelectedArtifact(record);
      toast.success(t('toast.ready'));
    },
    onFailed: (record: MethodologyArtifact) => {
      if (!isForActiveMethod(record)) return;
      setIsGenerating(false);
      setArtifacts((prev) => prev.map((a) => (a.id === record.id ? record : a)));
      setSelectedArtifact(record);
      toast.error(t('toast.generateError'));
    },
    onTimeout: () => {
      setIsGenerating(false);
      toast.error(t('toast.timeoutError'));
    },
  });

  // Switching methodology wipes the previous one entirely, not just the form.
  // React Router keeps this component mounted when only :methodId changes
  // (browser Back between two /methods/:id pages), and F-03 mounts it in tabs
  // where switching never remounts. Leaving the artifact and the generating
  // flag behind showed one methodology's result under another's heading, with
  // its overlay still spinning.
  // stopPolling is read through a ref so this effect depends on methodId
  // alone: a hook that returns a fresh callback each render would otherwise
  // re-run the reset on every render and loop.
  const stopPollingRef = useRef(stopPolling);
  stopPollingRef.current = stopPolling;
  const startPollingRef = useRef(startPolling);
  startPollingRef.current = startPolling;
  const polledArtifactIdRef = useRef<string | null>(null);

  // A pending record can outlive a page visit. Resume it on entry, and use the
  // same path for a freshly-created record so POST never starts a second
  // interval. Keep the id after timeout: a render must not silently restart a
  // poller that has already exhausted its time budget.
  useEffect(() => {
    if (selectedArtifact?.status !== 'pending' || polledArtifactIdRef.current === selectedArtifact.id) {
      return;
    }

    polledArtifactIdRef.current = selectedArtifact.id;
    setIsGenerating(true);
    startPollingRef.current(selectedArtifact.id);
  }, [selectedArtifact?.id, selectedArtifact?.status]);

  useEffect(() => {
    activeMethodIdRef.current = methodId;
    stopPollingRef.current();
    polledArtifactIdRef.current = null;
    setSelectedProjectId('');
    setSelectedPersonaId('');
    setInputValues({});
    setContextOverrides({});
    setSelectedArtifact(null);
    setArtifacts([]);
    setIsGenerating(false);
  }, [methodId]);

  const handleGenerate = async () => {
    if (!catalogMethod) return;
    if (catalogMethod.requiresProject && !selectedProjectId) {
      toast.error(t('empty.selectProject'));
      return;
    }

    setIsGenerating(true);
    try {
      const inputs: Record<string, string> = {};
      for (const [key, value] of Object.entries(inputValues)) {
        if (value.trim()) inputs[key] = value.trim();
      }

      const pendingArtifact = await methodologiesApi.generate({
        method: methodId,
        projectId: selectedProjectId || undefined,
        personaId: selectedPersonaId || undefined,
        inputs,
        ...(Object.keys(contextOverrides).length > 0 ? { sources: contextOverrides } : {}),
        lang,
      });

      setArtifacts((prev) => [pendingArtifact, ...prev]);
      setSelectedArtifact(pendingArtifact);
    } catch (error) {
      console.error('Error starting methodology generation:', error);
      toast.error(error instanceof Error ? error.message : t('toast.createError'));
      setIsGenerating(false);
    }
  };

  const handleDelete = async () => {
    if (!artifactToDelete) return;
    try {
      await methodologiesApi.delete(artifactToDelete);
      setArtifacts((prev) => prev.filter((a) => a.id !== artifactToDelete));
      setSelectedArtifact((prev) => (prev?.id === artifactToDelete ? null : prev));
      toast.success(t('toast.deleted'));
    } catch (error) {
      console.error('Error deleting methodology artifact:', error);
      toast.error(t('toast.deleteError'));
    } finally {
      setShowDeleteConfirm(false);
      setArtifactToDelete(null);
    }
  };

  const getArtifactTitle = (artifact: MethodologyArtifact): string => {
    if (artifact.title) return artifact.title;
    if (artifact.status === 'pending') return t('history.pending');
    if (artifact.status === 'failed') return t('history.failed');
    return t(`methods.${methodId}.title`);
  };

  const getArtifactSubtitle = (artifact: MethodologyArtifact): string => {
    const projectName = projects.find((p) => p.id === artifact.project)?.name ?? t('detail.defaultProjectName');
    const date = new Date(artifact.created).toLocaleDateString();
    return t('history.itemSubtitle', { projectName, date });
  };

  // Access rules already scope the list/delete endpoints to the owner. A plan
  // downgrade blocks new generation, not management of previously-created data.
  const canDelete = true;
  const noProjectsForRequired = Boolean(
    catalogMethod && !isFree && catalogMethod.available && catalogMethod.requiresProject && projects.length === 0,
  );

  if (isInitialLoading) {
    return (
      <div className="flex items-center justify-center py-[80px]">
        <div className="w-[32px] h-[32px] border-2 border-[#2197f1] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (hasLoadError) {
    return (
      <div className="flex flex-col items-center justify-center gap-[16px] py-[64px] px-[24px] bg-[#f4f4f3] rounded-[16px] text-center">
        <div className="flex flex-col gap-[8px] max-w-[520px]">
          <h2 className="font-manrope font-bold text-[24px] leading-[32px] text-[#0d0d0d]">
            {t('loadError.title')}
          </h2>
          <p className="font-segoe text-[16px] leading-[24px] text-[#565858]">
            {t('loadError.description')}
          </p>
        </div>
        <button
          type="button"
          onClick={loadData}
          className="h-[48px] px-[24px] rounded-[16px] bg-[#2197f1] hover:bg-[#1a7acc] font-segoe font-semibold text-[16px] leading-[24px] text-white transition-colors"
        >
          {t('loadError.retry')}
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-[24px]">
      <div className="flex flex-col lg:flex-row gap-[24px] items-start">
        <div className="flex flex-col gap-[16px] w-full lg:w-[380px] lg:flex-shrink-0">
          {catalogMethod && (
            noProjectsForRequired ? (
              <p className="font-segoe text-[14px] leading-[20px] text-[#565858] bg-white rounded-[16px] border border-[#eeeeee] p-[24px]">
                {t('empty.noProjects')}
              </p>
            ) : (
              <MethodologyGenerateForm
                catalogMethod={catalogMethod}
                isFree={isFree}
                projects={projects}
                personas={personas}
                selectedProjectId={selectedProjectId}
                onSelectProject={setSelectedProjectId}
                selectedPersonaId={selectedPersonaId}
                onSelectPersona={setSelectedPersonaId}
                inputValues={inputValues}
                onInputChange={handleInputChange}
                onContextChange={setContextOverrides}
                onGenerate={handleGenerate}
                isGenerating={isGenerating}
              />
            )
          )}

          {artifacts.length > 0 && (
            <div className="flex flex-col gap-[8px]">
              <p className="font-manrope font-semibold text-[13px] leading-[18px] text-[#565858] uppercase px-[8px]">
                {t('history.title')}
              </p>
              <AiItemsList
                items={artifacts}
                selectedId={selectedArtifact?.id ?? null}
                getItemTitle={getArtifactTitle}
                getItemSubtitle={getArtifactSubtitle}
                onSelect={setSelectedArtifact}
              />
            </div>
          )}
        </div>

        <div className="flex-1 min-w-0 w-full">
          {selectedArtifact ? (
            <MethodologyResultPanel
              methodId={methodId}
              catalogMethod={catalogMethod}
              artifact={selectedArtifact}
              projectName={projects.find((p) => p.id === selectedArtifact.project)?.name ?? ''}
              canDelete={canDelete}
              onReview={methodId === 'hadi' && !isGenerating ? () => {
                setSelectedProjectId(selectedArtifact.project ?? '');
                setInputValues({ ...selectedArtifact.inputs });
                document.getElementById('method-field-results')?.focus();
              } : undefined}
              onDelete={() => {
                setArtifactToDelete(selectedArtifact.id);
                setShowDeleteConfirm(true);
              }}
            />
          ) : (
            <div className="flex items-center justify-center py-[80px] bg-[#f4f4f3] rounded-[16px]">
              <p className="font-segoe text-[16px] leading-[24px] text-[#565858]">{t('empty.noArtifact')}</p>
            </div>
          )}
        </div>
      </div>

      {isGenerating && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-[24px] p-[40px] max-w-[480px] w-full mx-[16px]">
            <GenerationProgress
              title={t('generating.title')}
              message={t('generating.message')}
              estimatedDuration={90}
            />
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setArtifactToDelete(null);
        }}
        onConfirm={handleDelete}
        title={t('confirm.deleteTitle')}
        description={t('confirm.deleteDescription')}
        confirmText={t('confirm.deleteConfirm')}
        cancelText={t('confirm.deleteCancel')}
      />
    </div>
  );
}
